/* eslint-disable max-len */
import {
  find, propEq, compose, curry, findIndex, defaultTo, add, propOr,
} from 'ramda';
import { fallbackTo } from './helper';

const emptyChapter = {
  start: null,
  end: null,
  title: null,
  index: -1,
};

export const getChapterByIndex = chapters => index => propOr(emptyChapter, index, chapters);

export const currentChapterIndex = compose(
  fallbackTo(-1),
  findIndex(propEq('active', true)),
  defaultTo([]),
);
export const currentChapter = compose(
  fallbackTo(emptyChapter),
  find(propEq('active', true)),
  defaultTo([]),
);

export const nextChapter = chapters => compose(
  getChapterByIndex(chapters),
  add(1),
  currentChapterIndex,
)(chapters);

export const previousChapter = chapters => compose(
  getChapterByIndex(chapters),
  add(-1),
  currentChapterIndex,
)(chapters);

export const currentChapterByPlaytime = curry((chapters, playtime) => find((chapter) => {
  if (playtime < chapter.start) {
    return false;
  }

  if (playtime >= chapter.end) {
    return false;
  }

  return true;
})(chapters));

export const inactiveChapter = chapter => ({
  ...chapter,
  active: false,
});

export const activeChapter = chapter => ({
  ...chapter,
  active: true,
});

export const setActiveByPlaytime = playtime => (chapter) => {
  if (playtime < chapter.start) {
    return inactiveChapter(chapter);
  }

  if (playtime >= chapter.end) {
    return inactiveChapter(chapter);
  }

  return activeChapter(chapter);
};

export const setActiveByIndex = chapterIndex => (chapter, index) => (chapterIndex === index ? activeChapter(chapter) : inactiveChapter(chapter));

export const getShownListInternal = (allChapters, index, maxItems) => {
  const result = {
    showPrevChapterButton: true,
    showNextChapterButton: true,
    offset: 0,
  };

  // if less items than possible remove all buttons and show all
  if (allChapters.length <= maxItems) {
    return {
      showPrevChapterButton: false,
      showNextChapterButton: false,
      offset: 0,
    };
  }

  let shownItems = maxItems;
  if (index < 2) {
    result.offset = 0;
    result.showPrevChapterButton = false;
  } else {
    result.offset = index - 1;
    result.showPrevChapterButton = true;
    shownItems -= 1;
  }

  const lastPossibleIndex = allChapters.length - shownItems - 1;

  // if index is higher than max possible one set it to it to not create whitespace at the end
  if (result.offset >= lastPossibleIndex) {
    result.showNextChapterButton = false;
    result.offset = lastPossibleIndex;
  } else {
    result.showNextChapterButton = true;
  }

  return result;
};

const itemsPerPage = 7;
export const getShownList = (allChapters, index) => getShownListInternal(allChapters, index, itemsPerPage);

function getFirstShownIndex(offset, showPrevButton) {
  if (showPrevButton) {
    return offset + 1;
  }

  return offset;
}

function getLastShownIndex(offset, showNextButton, maxShowItems) {
  return (showNextButton ? -1 : 0) + offset + maxShowItems - 1;
}

export function getPrevPageIndex(currentOffset, showPrevButton) {
  return getFirstShownIndex(currentOffset, showPrevButton) - itemsPerPage + 2;
}

export function getNextPageIndex(currentOffset, showNextButton) {
  return getLastShownIndex(currentOffset, showNextButton, itemsPerPage) + 1;
}

export function chapterIsShown(chapterOffset, currentOffset, showPrevButton, showNextButton) {
  return chapterOffset >= getFirstShownIndex(currentOffset, showPrevButton) && chapterOffset <= getLastShownIndex(currentOffset, showNextButton, itemsPerPage);
}
